import { useCallback, useMemo } from 'react';
import { create } from 'zustand';
import { createJSONStorage, persist, subscribeWithSelector } from 'zustand/middleware';

import { Settings, TablePageSettings, TableSettings, TableTimeFormat } from '~/repositories/UserRepository';

export const PAGE_SIZE_OPTIONS = [5, 10, 15, 20, 25, 50];

const DEFAULT_TABLE_PAGE_SIZE = 10;

export const DEFAULT_TABLE_SETTINGS: TableSettings = {
  transfers: { limit: 10, timeFormat: TableTimeFormat.Relative },
  loanPayments: { limit: 5, timeFormat: TableTimeFormat.Relative },
  loanDisbursements: { limit: 5, timeFormat: TableTimeFormat.Relative },
};

interface ConfigStoreState {
  tableSettings: TableSettings;
}

interface ConfigStoreActions {
  loadTableSettings: (tableSettings: TableSettings | undefined) => void;
  setTableSettings: (tableId: string, settings: Partial<TablePageSettings>) => Promise<Partial<Settings>>;
}

export const useConfigStore = create<ConfigStoreState & ConfigStoreActions>()(
  subscribeWithSelector(
    persist(
      (set, get) => ({
        tableSettings: DEFAULT_TABLE_SETTINGS,

        loadTableSettings: (tableSettings) => {
          set({ tableSettings: { ...DEFAULT_TABLE_SETTINGS, ...(tableSettings ?? {}) } });
        },

        setTableSettings: async (tableId, partial) => {
          const current = get().tableSettings[tableId];
          const merged: TablePageSettings = {
            ...current,
            ...partial,
            limit: partial.limit ?? current?.limit ?? DEFAULT_TABLE_PAGE_SIZE,
          };
          const next: TableSettings = { ...get().tableSettings, [tableId]: merged };
          set({ tableSettings: next });
          const { UserRepository } = await import('~/repositories');
          return UserRepository.updateSettings({ tableSettings: next });
        },
      }),
      {
        name: 'useConfigStore',
        version: 1,
        storage: createJSONStorage(() => localStorage),
      }
    )
  )
);

export const useTableConfig = (tableId: string) => {
  const setTableSettings = useConfigStore((state) => state.setTableSettings);
  const pageSize = useConfigStore((state) => state.tableSettings[tableId]?.limit ?? DEFAULT_TABLE_PAGE_SIZE);
  const timeFormat = useConfigStore((state) => state.tableSettings[tableId]?.timeFormat ?? TableTimeFormat.Relative);

  const update = useCallback(
    (partial: Partial<TablePageSettings>) => setTableSettings(tableId, partial),
    [tableId, setTableSettings]
  );

  return useMemo(() => ({ pageSize, timeFormat, update }), [pageSize, timeFormat, update]);
};
